import {
  ExclamationMarkCircleFillIcon,
  FaroIconButton,
  FaroPopover,
  InfoIcon,
  grey,
  yellow,
} from "@faro-lotv/flat-ui";
import {
  PropsWithChildren,
  ReactNode,
  useRef,
  useState,
  type JSX,
} from "react";

export type HelpPopoverProps = {
  /** The title of the popover */
  title: string;

  /** The description of the popover */
  description: ReactNode;

  /** Function called when the icon button to open the popover is clicked */
  onClick?(): void;

  /** Variant of the help popover @default "info" */
  variant?: "info" | "darkInfo" | "warning";

  /** On which interaction to show the popover @default "click" */
  interaction?: "click" | "hover";
};

/**
 * @returns A help icon button that opens a popover with the given title and description
 */
export function HelpPopover({
  title,
  description,
  onClick,
  children,
  variant = "info",
  interaction = "click",
}: PropsWithChildren<HelpPopoverProps>): JSX.Element {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const color = getColor(variant);

  return (
    <>
      <FaroIconButton
        color={color}
        // As the color has a meaning for this icon it should not change on hover
        // the shadow is enough indication of the hovering
        hoverColor={color}
        size="xs"
        ref={buttonRef}
        onPointerEnter={() => {
          if (interaction !== "hover") return;
          setIsPopoverOpen(true);
        }}
        onPointerLeave={() => {
          if (interaction !== "hover") return;
          setIsPopoverOpen(false);
        }}
        onClick={(event) => {
          // Note: Intentionally still active when interaction is set to hover, to account for touch devices
          onClick?.();
          // Prevent e.g. interacting with form elements when activating the help
          event.stopPropagation();
          setIsPopoverOpen(true);
        }}
      >
        {variant === "info" || variant === "darkInfo" ? (
          <InfoIcon sx={{ width: "16px", height: "16px" }} />
        ) : (
          <ExclamationMarkCircleFillIcon
            sx={{ width: "16px", height: "16px" }}
          />
        )}
      </FaroIconButton>
      <FaroPopover
        title={title}
        description={description}
        dark
        open={isPopoverOpen}
        anchorEl={buttonRef.current}
        onClose={() => setIsPopoverOpen(false)}
      >
        {children}
      </FaroPopover>
    </>
  );
}

/**
 * @returns Button allowing user to learn more about the Waypoints Elevation and the showing of waypoints on floors
 */
export function ShowWaypointsOnFloorsHelp(): JSX.Element {
  return (
    <HelpPopover
      title="Show waypoints on floors"
      description="This option renders the waypoints on the floor, with a predefined offset. Turning this option off will show the waypoints at their real elevation."
    />
  );
}

function getColor(variant: string): string {
  switch (variant) {
    case "info":
      return grey[100];
    case "darkInfo":
      return grey[900];
    case "warning":
      return yellow[600];
    default:
      return grey[100];
  }
}
